/* eslint-disable no-underscore-dangle */
import {
  Component, OnInit, OnDestroy, ViewChild,
  EventEmitter
} from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators, AbstractControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import {
  filter, switchMap, takeWhile, tap,
  takeUntil
} from 'rxjs/operators';

import { PatientService } from '@sondermind/data-access/patient';
import { ReferenceDatum, ReferenceDataStore } from '@sondermind/data-access/reference-data';
import { SondermindApplicationService } from '@sondermind/data-access/sondermind-app-service';
import { AlertModalComponent } from '@sondermind/features/modals/alert';
import {
  ClientAccountRequiredModalComponent,
  ClientAccountPendingModalComponent
} from '@sondermind/features/modals/client-account';

import { ConfirmationModalComponent } from '@sondermind/features/modals/confirmation';
import { ContactModalComponent } from '@sondermind/features/modals/contact';
import { IAPIFilterOption, ServerResponse } from '@sondermind/utilities/models-http';
import { Patient } from '@sondermind/utilities/models-patient';
import { PatientContact } from '@sondermind/utilities/models-patient-contact';
import { FieldMaskingService } from '@sondermind/utilities/services/field-masking.service';

import { EmailValidator } from '@sondermind/utilities/tools/validators/email.validator';
import { PastDateValidator } from '@sondermind/utilities/tools/validators/past-date.validator';
import { PhoneValidator } from '@sondermind/utilities/tools/validators/phone.validator';

import { DropdownOption } from '@sondermind/iris/dropdown';
import { IrisButtonTypes } from '@sondermind/iris/button';
import { IrisModalService, IrisModalSize } from '@sondermind/iris/modal';
import { ClientDetailPageService } from '@sondermind/features/provider-facing/client-detail';
import { IrisIconButtonTypes } from '@sondermind/iris/icon-button';
import { PatientContactsService } from '@sondermind/core/services/http/clinical/patient/patient-contacts.httpservice';

import {
  ClientActivation,
  ClientTerminationReasonsModalService
} from '../../services/client-termination-reasons/client-termination-reasons-modal.service';

interface IClientContactFormModel {
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  dateOfBirth: FormControl<string | Date | null>;
  genderId: FormControl<number | string | null>;
  email: FormControl<string | null>;
  cellPhone: FormControl<string | null>;
  homePhone: FormControl<string | null>;
  addressLine1: FormControl<string | null>;
  addressLine2: FormControl<string | null>;
  city: FormControl<string | null>;
  state: FormControl<string | null>;
  postalCode: FormControl<string | null>;
}

@Component({
  selector: 'member-client-detail-contact-info',
  templateUrl: './client-detail-contact-info.component.html',
  styleUrls: ['./client-detail-contact-info.component.scss'],
  standalone: false
})
export class ClientDetailContactInfoPage implements OnInit, OnDestroy {
  @ViewChild(ConfirmationModalComponent)
    confirmModal: ConfirmationModalComponent;

  @ViewChild(AlertModalComponent)
    alertModal: AlertModalComponent;

  @ViewChild(ContactModalComponent)
    contactModal: ContactModalComponent;

  private destroyed$ = new EventEmitter<void>();

  patient: Patient;
  patientContacts: PatientContact[] = [];
  patientSub: Subscription;

  form: FormGroup<IClientContactFormModel>;
  formReset: FormGroup<IClientContactFormModel>;

  saving = false;
  loading = true;
  modalVisible = false;

  genders$: Observable<ReferenceDatum[]>;
  usStateNames$: Observable<ReferenceDatum[]>;
  genderOptions: Array<DropdownOption<number>> = [];
  usStateNamesOptions: Array<DropdownOption<string>> = [];

  irisButtonTypes = IrisButtonTypes;
  irisIconButtonTypes = IrisIconButtonTypes;

  constructor(
    private appService: SondermindApplicationService,
    private patientService: PatientService,
    private patientContactService: PatientContactsService,
    private snackBar: MatSnackBar,
    private clientDetailPageService: ClientDetailPageService,
    private clientTerminationReasonsModalService: ClientTerminationReasonsModalService,
    private referenceDataStore: ReferenceDataStore,
    private builder: FormBuilder,
    public _maskingService: FieldMaskingService,
    private irisModalService: IrisModalService
  ) {
    this.form = this.builder.group<IClientContactFormModel>({
      firstName: new FormControl<string | null>(null),
      lastName: new FormControl<string | null>(null),
      dateOfBirth: new FormControl<string | Date | null>(null, Validators.compose([PastDateValidator.format()])),
      genderId: new FormControl<number | string | null>(null),
      email: new FormControl<string | null>(null, Validators.compose([EmailValidator.format])),
      cellPhone: new FormControl<string | null>(null, Validators.compose([PhoneValidator.format])),
      homePhone: new FormControl<string | null>(null, Validators.compose([PhoneValidator.format])),
      addressLine1: new FormControl<string | null>(null),
      addressLine2: new FormControl<string | null>(null),
      city: new FormControl<string | null>(null),
      state: new FormControl<string | null>(null),
      postalCode: new FormControl<string | null>(null)
    }, {
      validators: (form: AbstractControl) => {
        const first = form.get('firstName')?.value;
        const last = form.get('lastName')?.value;

        if ((first && first.trim().length >= 1) || (last && last.trim().length >= 1)) {
          return null;
        }

        return {
          nameMissing: true,
          firstName: 'A first or last name is required!',
        };
      }
    });
  }

  initializeReferenceData(): void {
    this.genders$ = this.referenceDataStore.getGenders$();
    this.usStateNames$ = this.referenceDataStore.getUsStateNames$();

    this.genders$.pipe(
      filter((res) => !!res?.length),
      takeUntil(this.destroyed$)
    ).subscribe((res) => {
      this.genderOptions = [
        ...res.map((r) => ({
          name: r.title,
          value: r.id
        }))
      ];
    });

    this.usStateNames$.pipe(
      filter((res) => !!res?.length),
      takeUntil(this.destroyed$)
    ).subscribe((res) => {
      this.usStateNamesOptions = [
        ...res.map((r) => ({
          name: r.title,
          value: r.tag
        }))
      ];
    });
  }

  ngOnInit(): void {
    this.initializeReferenceData();
    this.patientSub = this.clientDetailPageService.patient
      .pipe(
        filter((p) => p !== null),
        switchMap((p) => forkJoin([
          of(p),
          this.patientContactService.list({ filter: this.filterParams(p) })
        ]))
      )
      .subscribe(([patient, patientContacts]) => {
        if (this.patient !== patient) {
          this.patient = patient;
          this.resetForm();
        }

        this.patientContacts = patientContacts.data;

        this.loading = false;
      });
  }

  ngOnDestroy(): void {
    if (this.patientSub) {
      this.patientSub.unsubscribe();
    }
    this.destroyed$.next();
  }

  handleCreateContact(event): void {
    this.contactModal.openForCreate().subscribe((c) => {
      if (c !== null) {
        this.reloadContacts();
      }
    });
  }

  handleActiveClick(event): void {
    const confirm = this.confirmModal.openThen(
      'Confirm Activation',
      'Are you sure you want to make this client active?',
      'Activate'
    );

    this.doActivate(confirm, true);
  }

  handleInactiveClick(event): void {
    if (!this.patient.canInactivate) {
      this.snackBar.open('Please submit your client\'s Claim(s) before inactivating the client.', 'Dismiss');
      return;
    }
    this.modalVisible = true;
    this.clientTerminationReasonsModalService.setSelectedPatientId(this.patient.id);
    this.clientTerminationReasonsModalService.toggleModal();
    this.clientTerminationReasonsModalService.subscribeToModalEvents().pipe(
      takeWhile(() => this.modalVisible),
    ).subscribe((status) => {
      this.modalVisible = status.visibility;
      if (status.clientActivation && status.clientActivation === ClientActivation.INACTIVE) {
        this.handleClientActivation(false).subscribe();
      }
    });
  }

  handleCancel(): void {
    this.form.reset(this.formReset.value);
  }

  handleSave(event): void {
    if (this.form.invalid) {
      this.form.markAsTouched();
      return;
    }

    this.saving = true;

    const p = new Patient();
    const data = this.form.value;

    p.id = this.patient.id;
    p.firstName = data.firstName;
    p.lastName = data.lastName;
    p.email = data.email;
    p.cellPhone = data.cellPhone;
    p.homePhone = data.homePhone;
    p.addressLine1 = data.addressLine1;
    p.addressLine2 = data.addressLine2;
    p.city = data.city;
    p.state = data.state;
    p.postalCode = data.postalCode;
    p.genderId = typeof data.genderId === 'number' ? data.genderId.toString() : data.genderId;
    p.dateOfBirth = data.dateOfBirth instanceof Date ? data.dateOfBirth.toISOString() : data.dateOfBirth;

    // due to how display name is updated with legal name in rails
    // an extra call to get patient is required in order to update the patient name header without refreshing
    this.patientService.update(this.patient.userId, p).pipe(
      switchMap(() => this.patientService.load(this.patient.id))
    ).subscribe((sr) => {
      this.saving = false;

      this.patient = sr.value;
      this.clientDetailPageService.patientUpdated(this.patient);

      this.resetForm();
    });
  }

  reloadContacts(): void {
    this.loading = true;
    this.patientContactService
      .list({ filter: this.filterParams(this.patient) })
      .subscribe({
        next: (sr) => {
          this.patientContacts = sr.data;
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          // eslint-disable-next-line no-console
          console.error(err);
        }
      });
  }

  billViaSondermind(): void {
    if (!this.patient.nonSpnPatient) {
      this.snackBar.open('Bill via Sondermind is already enabled!', 'Dismiss');
      return;
    }

    this.patientService.toggleNonSpnPatient(
      this.patient.userId,
      this.patient
    ).subscribe((sr) => {
      this.patient = sr.value;
      this.snackBar.open('Bill via Sondermind is enabled', 'Dismiss');
      this.clientDetailPageService.patientUpdated(this.patient);
    });
  }

  accountRequired(): void {
    if (!this.patient.accountRequired) {
      this.snackBar.open('Account is already there or not required for this client!', 'Dismiss');
      return;
    }

    const irisModalRef = this.irisModalService.open<ClientAccountRequiredModalComponent, { patient: Patient; }, Patient>(ClientAccountRequiredModalComponent, {
      data: { patient: this.patient },
      size: IrisModalSize.MD
    });

    irisModalRef.afterClosed$().subscribe((patient) => {
      if (patient) {
        this.patient = patient;
        this.clientDetailPageService.patientUpdated(this.patient);
      }
    });
  }

  accountPending(): void {
    if (!this.patient.accountPending) {
      this.snackBar.open('Account is not pending a client account', 'Dismiss');
      return;
    }

    const irisModalRef = this.irisModalService.open<ClientAccountPendingModalComponent, { patient: Patient; }, Patient>(ClientAccountPendingModalComponent, {
      data: { patient: this.patient },
      size: IrisModalSize.MD
    });

    irisModalRef.afterClosed$().subscribe((patient) => {
      if (patient) {
        this.patient = patient;
        this.clientDetailPageService.patientUpdated(this.patient);
      }
    });
  }

  private doActivate(confirm: Observable<any>, active: boolean) {
    confirm
      .pipe(
        filter((r) => !!r),
        switchMap(() => this.handleClientActivation(active)),
      )
      .subscribe();
  }

  private resetForm(): void {
    this.form.setValue({
      firstName: this.patient.firstName,
      lastName: this.patient.lastName,
      dateOfBirth: this.patient.dateOfBirth,
      genderId: this.patient.genderId,
      email: this.patient.email,
      cellPhone: this.patient.cellPhone,
      homePhone: this.patient.homePhone,
      addressLine1: this.patient.addressLine1,
      addressLine2: this.patient.addressLine2,
      city: this.patient.city,
      state: this.patient.state,
      postalCode: this.patient.postalCode,
    });

    this.formReset = this.form;

    if (this.patient.haveClientRecord) {
      this.form.get('email')?.disable();
    }
  }

  // Return the object of filter params
  private filterParams(patient: Patient): IAPIFilterOption {
    if (patient.haveClientRecord) {
      return { key: 'persona_client_id', value: `${patient.personaClientId}` };
    }
    return { key: 'patient_id', value: `${patient.id}` };
  }

  private handleClientActivation(active: boolean): Observable<ServerResponse<Patient, any>> {
    return this.patientService.activeInactive(this.patient.userId, this.patient.id, active)
      .pipe(
        tap((sr) => {
          this.patient = sr.value;
          this.clientDetailPageService.patientUpdated(this.patient);
        })
      );
  }
}
